import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Grid, Divider, Typography, Button, Stack, Container } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import { StoreApi } from '../api/storeApi';
import { FulfillmentCenterAPI } from '../api/fulfillmentCenterApi';
import * as notificationActions from '../../../actions/notificationAction';
import BackButton from '@core-ui-shared/BackButton';
import FulfillmentCenterList from '../components/FulfillmentCenterList';
import AuthenticationForm from '../components/AuthenticationForm';
import { LogisticsProvider, useLogistics } from '../contexts/logisticsContext';
import { Tile, BulkStockSyncModal, StoreProfile } from '../../../shared-components';
import useTranslation from '../../../hooks/useTranslation';
import { Link } from 'react-router-dom';
import { ProductApi } from '../../../api/productApi';
import AuthStorage from '../../../services/authStorage';

const styles = {
  paper: {
    paddingRight: 0,
    width: '100%',
    border: '1px solid lightgrey'
  },
  actionBtn: {
    marginLeft: '0.75rem',
    borderColor: 'lightgrey'
  }
};

const Inner = (props) => {
  const {
    selectedCenter,
    setSelectedCenter,
    applyOnOrderStatus,
    setApplyOnOrderStatus,
    customOrderStatus,
    isShelfless,
    setIsShelfless,
    dreamUsername,
    setDreamUsername,
    dreamPassword,
    setDreamPassword,
    showCredentials,
    setShowCredentials,
    setShowAuthenticationForm,
    setAuthenticating,
    setFulfillmentCenters,
    store,
    setStore,
    autoStockSync,
    setAutoStockSync,
    autoTrackingSync,
    setAutoTrackingSync,
    setFulfillmentCenterType,
    isThirdParty
  } = useLogistics();

  const { t } = useTranslation();
  const Auth = new AuthStorage();

  const [stats, setStats] = useState({
    products: { displayValue: 0, difference: 0 },
    users: { displayValue: 0, difference: 0 },
    orders: { latestOrders: [], displayValue: 0, difference: 0 },
    sales: { displayValue: 0 }
  });
  const [showFulfillmentModal, setShowFulfillmentModal] = useState(false);
  const [syncOpen, setSyncOpen] = useState(false);
  const [stockData, setStockData] = useState([]);
  const [syncLoading, setSyncLoading] = useState(false);

  const storeCurrency = useMemo(() => ({ name: 'Swedish Krone', symbol: 'kr', isoCode: 'sek' }), []);
  const hasStockProvider = useMemo( () => store?.fulfillmentCenters?.some((fc) => ['shelfless', 'on-demand'].includes(fc.system_name)), [store] );

  const fetchStore = async () => {
    try {
      const response = await StoreApi.getStore(props.storeId);
      setStore(response.data);
      setFulfillmentCenters(response.data.fulfillmentCenters || []);
    } catch (error) {
      props.notifyActions.setErrorNotification({ message: error.message });
    }
  };

  const fetchAllFulfillmentCenters = async () => {
    try {
      const response = await FulfillmentCenterAPI.getFulfillmentCenters({ page: -1 });
      setFulfillmentCenters(response.data.data);
    } catch (error) {
      props.notifyActions.setErrorNotification({ message: error.message });
    }
  };

  const calculateDifference = (object) => {
    if (object.newCount === object.lastMonthCount) return 0;
    if (object.newCount === 0 && object.lastMonthCount > 0) return -100;
    if (object.newCount > 0 && object.lastMonthCount === 0) return 100;
    return parseInt(((object.newCount - object.lastMonthCount) / object.lastMonthCount) * 100);
  };

  const fetchStats = async () => {
    try {
      const result = await StoreApi.getStats(props.storeId);
      setStats({
        products: { displayValue: result.data.products.totalCount, difference: calculateDifference(result.data.products) },
        users: { displayValue: result.data.users.newCount, difference: calculateDifference(result.data.users) },
        orders: {
          displayValue: result.data.orders.newCount,
          difference: calculateDifference(result.data.orders),
          latestOrders: result.data.orders.latestOrders
        },
        sales: { displayValue: result.data.totalSales }
      });
    } catch (error) {}
  };

  useEffect(() => {
    fetchStore();
    fetchAllFulfillmentCenters();
    fetchStats();
  }, [props.storeId]);

  const handleAddFulfillmentCenter = async (centerId) => {
    try {
      await StoreApi.addFulfillmentCenterToStore(props.storeId, centerId);
      fetchStore();
      fetchAllFulfillmentCenters();
      props.notifyActions.setSuccessNotification({ message: t('response_messages.fulfillment_center_added') });
    } catch (error) {
      props.notifyActions.setErrorNotification({ message: t('response_messages.error_failed_to_add_fulfillment_center') });
    }
  };

  const handleRemoveFulfillmentCenter = async (centerId) => {
    try {
      await StoreApi.removeFulfillmentCenterFromStore(props.storeId, centerId);
      fetchStore();
      fetchAllFulfillmentCenters();
      props.notifyActions.setSuccessNotification({ message: t('response_messages.fulfillment_center_removed') });
    } catch (error) {
      props.notifyActions.setErrorNotification({ message: t('response_messages.error_failed_to_remove_fulfillment_center') });
    }
  };

  const primeCenterState = (center) => {
    setSelectedCenter(center);

    setIsShelfless(center.system_name === 'shelfless');

    setApplyOnOrderStatus(center.StoreFulfillmentCenters?.applyOnOrderStatus || 'default');
    setAutoStockSync(!!center.StoreFulfillmentCenters?.auto_stock_sync);
    setAutoTrackingSync(!!center.StoreFulfillmentCenters?.auto_tracking_sync);

    setFulfillmentCenterType(center.provider_type);
    setShowAuthenticationForm(true);

    const isThirdPartyCenter = center.provider_type === 'third-party';
    const hasStoredCreds = !!center.StoreFulfillmentCenters?.username;
    const shouldShowCredInputs = !(isThirdPartyCenter && hasStoredCreds);
    setShowCredentials(shouldShowCredInputs);
    setDreamUsername('');
    setDreamPassword('');
  };

  const handleActivate = (center) => primeCenterState(center);
  const handleEdit = (center) => primeCenterState(center);

  const handleDeactivate = async (center) => {
    try {
      await StoreApi.authenticateLogistics(
        props.storeId,
        center.id,
        applyOnOrderStatus === 'customized' ? customOrderStatus : applyOnOrderStatus,
        'inactive'
      );
      fetchStore();
      fetchAllFulfillmentCenters();
      props.notifyActions.setSuccessNotification({ message: t('response_messages.fulfillment_center_deactivated') });
    } catch (error) {
      props.notifyActions.setErrorNotification({ message: t('response_messages.fulfillment_center_activation_failed') });
    }
  };

  const handleAuthenticate = async () => {
    if (isThirdParty && showCredentials && (!dreamUsername || !dreamPassword)) {
      props.notifyActions.setErrorNotification({ message: t('response_messages.username_password_required') });
      return;
    }

    setAuthenticating(true);
    try {
      const status = 'active';
      const finalStatus = applyOnOrderStatus === 'customized' ? customOrderStatus : applyOnOrderStatus;

      if (isThirdParty) {
        await StoreApi.authenticateLogistics(
          props.storeId,
          selectedCenter.id,
          finalStatus,
          status,
          {
            ...(showCredentials ? { username: dreamUsername, password: dreamPassword } : {}),
            auto_stock_sync: autoStockSync,
            auto_tracking_sync: autoTrackingSync
          }
        );
      } else {
        await StoreApi.authenticateLogistics(props.storeId, selectedCenter.id, finalStatus, status);
      }

      props.notifyActions.setSuccessNotification({ message: t('response_messages.fulfillment_center_activation_success') });
      setShowAuthenticationForm(false);
      fetchStore();
      fetchAllFulfillmentCenters();
    } catch (error) {
      props.notifyActions.setErrorNotification({ message: t('response_messages.fulfillment_center_activation_failed') });
    }
    setAuthenticating(false);
  };

  const handleRevokeCredentials = () => {
    setShowCredentials(true);
    setDreamUsername('');
    setDreamPassword('');
  };

  const handleSyncOpen = async () => {
    setSyncOpen(true);
    setSyncLoading(true);
    try {
      const response = await ProductApi.fetchBulkStock(props.storeId);
      if (response.status === 200) {
        setStockData(response.data.products);
      }
    } catch (err) {
      props.notifyActions.setErrorNotification({ message: t('response_messages.bulk_stock_fetch_failed') });
    } finally {
      setSyncLoading(false);
    }
  };

  const handleSyncConfirm = async (selectedSkus = []) => {
    try {
      await ProductApi.bulkSyncStock({ skus: selectedSkus, store_id: props.storeId });
      props.notifyActions.setSuccessNotification({ message: t('response_messages.bulk_stock_sync_success') });
    } catch (error) {
      props.notifyActions.setErrorNotification({ message: t('response_messages.bulk_stock_sync_failed') });
    }
    setSyncOpen(false);
  };

  const storeUrl = useMemo(() => (store ? new AuthStorage().getStoreUrl(store) : '#'), [store]);

  return (
    <Container>
      <Stack spacing={3}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap">
          <Typography variant="h4" fontWeight={600}>
            {store?.name}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="flex-end">
            <BackButton />
            <Button
              variant="outlined"
              href={`${process.env.REACT_APP_STORE_ADMIN_LOGIN_URL}/store-auth/${localStorage.getItem('auth_token')}/true/${store?.id}`}
              target="_blank"
            >
              Admin Portal
              <OpenInNewIcon sx={{ marginLeft: '3px', fontSize: '14px' }} />
            </Button>
            <Button variant="outlined" href={Auth.getStoreUrl(store)} target="_blank">
              View Store
              <OpenInNewIcon sx={{ marginLeft: '3px', fontSize: '14px' }} />
            </Button>
            <Button
              sx={styles.actionBtn}
              variant="outlined"
              component={Link}
              to={`/admin/stores/${store?.id}/reports/sales-by-product`}
            >
              Report
            </Button>
            <Button
              sx={styles.actionBtn}
              variant="outlined"
              component={Link}
              to={{
                pathname: `/admin/stores/${store?.id}/edit`,
                state: { storeName: store?.name }
              }}
              startIcon={<EditIcon />}
            >
              {t('response_messages.edit')}
            </Button>
          </Stack>
        </Stack>

        <Grid container spacing={3}>
          <Tile title={t('labels.sales_last_month')} object={stats.sales} module={t('labels.profit')} storeCurrency={storeCurrency} />
          <Tile title={t('labels.orders_last_month')} object={stats.orders} module={t('labels.order')} />
          <Tile title={t('labels.new_users')} object={stats.users} module={t('labels.user')} />
          <Tile title={t('labels.active_products')} object={stats.products} module={t('labels.product')} />
        </Grid>

        <StoreProfile
          t={t}
          store={store}
          storeUrl={storeUrl}
          onEdit={() =>
            props.history.push({
              pathname: `/admin/stores/${store.id}/edit`,
              state: { storeName: store.name }
            })
          }
          onGeneralSettings={() =>
            props.history.push({
              pathname: `/admin/stores/${store.id}/edit`,
              state: { storeName: store.name }
            })
          }
          onPaymentSettings={() => props.history.push(`/admin/stores/${store.id}/payment-settings`)}
          onNotificationSettings={() => props.history.push(`/admin/stores/${store.id}/notification-settings`)}
          onAuthenticationSettings={() => props.history.push(`/admin/stores/${store.id}/auth-settings`)}
          showSettings={{ general: true, payment: true, notifications: true, authenticationSettings: true }}
          extraSettings={[
            {
              key: 'shipping_profiles',
              title: t('labels.manage_shipping_profiles'),
              description: t('labels.manage_shipping_profiles_desc'),
              icon: <LocalShippingIcon />,
              onClick: () =>
                props.history.push({
                  pathname: `/admin/stores/${store.id}/shipping-profiles`,
                  state: { storeName: store.name }
                })
            },
            {
              key: 'fulfillment_centers',
              title: t('labels.manage_fulfillment_centers'),
              description: t('labels.manage_fulfillment_centers_desc'),
              icon: <WarehouseIcon />,
              onClick: () => setShowFulfillmentModal(true)
            },
            {
              key: 'sync_stock',
              title: t('labels.sync_stock'),
              description: t('labels.sync_stock_desc'),
              icon: <Inventory2OutlinedIcon />,
              onClick: handleSyncOpen,
              disabled: !hasStockProvider
            }
          ]}
          rightHeaderActions={<Stack direction="row" spacing={1} />}
        />

        <AuthenticationForm onAuthenticate={handleAuthenticate} onRevokeCredentials={handleRevokeCredentials} />
        <Divider />

        <FulfillmentCenterList
          open={showFulfillmentModal}
          onClose={() => setShowFulfillmentModal(false)}
          onFulfillmentCenterSelected={handleAddFulfillmentCenter}
          onRemoveFulfillmentCenterClicked={handleRemoveFulfillmentCenter}
          handleActivate={handleActivate}
          handleDeactivate={handleDeactivate}
          handleEdit={handleEdit}
        />
      </Stack>

      <BulkStockSyncModal
        isOpen={syncOpen}
        onClose={() => setSyncOpen(false)}
        stockData={stockData}
        loading={syncLoading}
        onConfirm={handleSyncConfirm}
      />
    </Container>
  );
};

const StoreShowContainerWithProvider = (props) => (
  <LogisticsProvider>
    <Inner {...props} />
  </LogisticsProvider>
);

const mapStateToProps = (state, ownProps) => {
  const storeId = Number(ownProps.match.params.id);
  return { storeId };
};

const mapDispatchToProps = (dispatch) => ({
  notifyActions: bindActionCreators(notificationActions, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(StoreShowContainerWithProvider);